import {
  carouselSelectContentToDataLayer,
  getCurrentPageType,
  type PromotionData,
  selectPromotionToDataLayer,
  viewPromotionToDataLayer,
} from "@/utils/gtm.utils";

const SLIDE_SELECTOR = '[aria-roledescription="slide"]';
const ACTIVE_SLIDE_SELECTOR = `${SLIDE_SELECTOR}:not([aria-hidden="true"])`;
const PROMO_SELECTOR = "[data-promo-name]";
const CAROUSEL_SELECTOR = "[data-carousel-name]";
const CONTROL_SELECTOR = "[data-carousel-control]";
const CTA_SELECTOR = "[data-promo-cta]";
const TAB_SELECTOR = '[role="tab"]';
const MIN_VISIBLE_RATIO = 0.5;
export const REVEAL_DELAY_MS = 700;

type InteractionType = "dot" | "prev" | "next" | "tab";

/** "Spark DC – Hero Section" -> "spark_dc_hero_section". */
export function toSnakeCase(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getSlot(promo: HTMLElement): string {
  const type = promo.dataset.promoType ?? "hero";
  const explicit = promo.dataset.promoPosition;

  if (explicit) {
    return `${type}_${explicit}`;
  }

  const sameType = Array.from(
    document.querySelectorAll<HTMLElement>(PROMO_SELECTOR)
  ).filter((element) => element.dataset.promoType === type);

  return `${type}_${sameType.indexOf(promo) + 1}`;
}

function buildPromotion(promo: HTMLElement, locationId: string): PromotionData {
  return {
    promotion_name: promo.dataset.promoName ?? "",
    creative_name: promo.dataset.promoCreative,
    creative_slot: getSlot(promo),
    location_id: locationId,
  };
}

/** A promo inside an inactive carousel slide is not on screen. */
export function isPromoActive(promo: HTMLElement): boolean {
  const slide = promo.closest(SLIDE_SELECTOR);
  return !slide || slide.getAttribute("aria-hidden") !== "true";
}

/** Tracks impressions, promotion clicks and carousel interactions. */
export function createPromotionTracker(locationId: string) {
  const lastViewed = new WeakMap<HTMLElement, number>();
  const visible = new WeakSet<HTMLElement>();

  const fireView = (promo: HTMLElement) => {
    lastViewed.set(promo, Date.now());
    viewPromotionToDataLayer(buildPromotion(promo, locationId));
  };

  const maybeView = (promo: HTMLElement) => {
    if (visible.has(promo) && isPromoActive(promo) && !lastViewed.has(promo)) {
      fireView(promo);
    }
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const promo = entry.target as HTMLElement;
        const onScreen =
          entry.isIntersecting &&
          (entry.intersectionRatio >= MIN_VISIBLE_RATIO ||
            entry.intersectionRect.height >=
              window.innerHeight * MIN_VISIBLE_RATIO);

        if (onScreen) {
          visible.add(promo);
          maybeView(promo);
        } else {
          visible.delete(promo);
        }
      }
    },
    { threshold: [0, MIN_VISIBLE_RATIO, 1] }
  );

  const observed = new WeakSet<HTMLElement>();
  const observeAll = () => {
    for (const promo of document.querySelectorAll<HTMLElement>(
      PROMO_SELECTOR
    )) {
      if (!observed.has(promo)) {
        observed.add(promo);
        intersectionObserver.observe(promo);
      }
    }
  };

  // Slides become active by flipping aria-hidden; late-rendered promos add nodes.
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        const slide = mutation.target as HTMLElement;
        for (const promo of slide.querySelectorAll<HTMLElement>(
          PROMO_SELECTOR
        )) {
          maybeView(promo);
        }
      }
    }
    observeAll();
  });

  const trackCarouselClick = (control: HTMLElement, root: HTMLElement) => {
    const interaction = control.dataset.carouselControl as InteractionType;
    const contentId = toSnakeCase(root.dataset.carouselName ?? "");
    const base = {
      content_id: contentId,
      interaction_type: interaction,
      page_type: getCurrentPageType(),
    };

    if (root.dataset.carouselKind === "tabs") {
      trackTabClick(control, root, base);
      return;
    }

    const slides = Array.from(
      root.querySelectorAll<HTMLElement>(SLIDE_SELECTOR)
    );
    const active = root.querySelector<HTMLElement>(ACTIVE_SLIDE_SELECTOR);
    const promo = active?.querySelector<HTMLElement>(PROMO_SELECTOR);
    const clickedAt = Date.now();

    carouselSelectContentToDataLayer({
      ...base,
      visible_index: active ? slides.indexOf(active) + 1 : undefined,
      total_slides: slides.length,
      ...(promo && {
        promotion_id: toSnakeCase(promo.dataset.promoName ?? ""),
        promotion_name: promo.dataset.promoName,
        creative_name: promo.dataset.promoCreative,
        creative_slot: getSlot(promo),
      }),
    });

    // The slide revealed by the click gets its own impression.
    window.setTimeout(() => {
      const revealed = root
        .querySelector<HTMLElement>(ACTIVE_SLIDE_SELECTOR)
        ?.querySelector<HTMLElement>(PROMO_SELECTOR);

      if (
        revealed &&
        visible.has(revealed) &&
        (lastViewed.get(revealed) ?? 0) < clickedAt
      ) {
        fireView(revealed);
      }
    }, REVEAL_DELAY_MS);
  };

  const emitTab = (
    base: Record<string, unknown>,
    tabName: string,
    visibleIndex: number
  ) => {
    carouselSelectContentToDataLayer({
      ...base,
      tab_name: tabName,
      item_list_id: `home_${base.content_id}_${toSnakeCase(tabName)}`,
      visible_index: visibleIndex,
    });
  };

  const trackTabClick = (
    control: HTMLElement,
    root: HTMLElement,
    base: Record<string, unknown>
  ) => {
    if (control.dataset.carouselControl === "tab") {
      const tabs = Array.from(
        control.closest('[role="tablist"]')?.querySelectorAll(TAB_SELECTOR) ??
          []
      );
      emitTab(base, control.dataset.tabName ?? "", tabs.indexOf(control) + 1);
      return;
    }

    // Arrows (or slider controls inside a panel) report the tab they land on.
    window.setTimeout(() => {
      const tablist = Array.from(
        root.querySelectorAll<HTMLElement>('[role="tablist"]')
      ).find((list) => list.offsetParent !== null);
      const tabs = Array.from(
        tablist?.querySelectorAll<HTMLElement>(TAB_SELECTOR) ?? []
      );
      const activeTab = tabs.find(
        (tab) => tab.getAttribute("aria-selected") === "true"
      );

      if (activeTab) {
        emitTab(
          base,
          activeTab.dataset.tabName ?? "",
          tabs.indexOf(activeTab) + 1
        );
      }
    }, 0);
  };

  const handleClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const control = event.target.closest<HTMLElement>(CONTROL_SELECTOR);
    const root = control?.closest<HTMLElement>(CAROUSEL_SELECTOR);

    if (control && root) {
      trackCarouselClick(control, root);
      return;
    }

    const cta = event.target.closest<HTMLElement>(CTA_SELECTOR);
    const promo = cta?.closest<HTMLElement>(PROMO_SELECTOR);

    if (cta && promo) {
      selectPromotionToDataLayer(buildPromotion(promo, locationId), {
        click_element: "CTA",
        click_element_name: cta.textContent?.trim() ?? "",
        link_target: cta.dataset.linkTarget,
        link_target_type: cta.dataset.linkType,
      });
    }
  };

  observeAll();
  mutationObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["aria-hidden"],
    childList: true,
    subtree: true,
  });
  // Capture phase: menus and routes may unmount the target on click.
  document.addEventListener("click", handleClick, true);

  return () => {
    intersectionObserver.disconnect();
    mutationObserver.disconnect();
    document.removeEventListener("click", handleClick, true);
  };
}
